export const professionalNetworkingModule = {
  id: "ca-professional-networking",
  title: "Strategic Professional Networking",
  description: "Build authentic professional relationships that accelerate career growth and create mutual value.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Build authentic professional relationships that provide mutual value over time
- Leverage industry events, online platforms, and workplace interactions for networking success
- Maintain and nurture professional networks systematically and efficiently
- Access hidden job markets and opportunities through strategic relationship building

## Prerequisites
- Current professional role or recent work experience
- Access to professional networking platforms (LinkedIn, industry associations)

## Overview
85% of jobs are filled through networking, yet most professionals approach relationship building reactively. Strategic networking creates sustainable career advantages through authentic relationships, industry visibility, and access to opportunities before they're publicly available. This module teaches relationship building strategies used by top executives and entrepreneurs.

---

## Key Concepts

### The Network Portfolio Approach
Effective networking requires intentional diversity across relationship types, industries, and career stages. Your network portfolio should include mentors, peers, mentees, and industry connections.

#### Why It Matters
Diverse networks provide 5x more career opportunities than homogeneous ones. Different relationship types serve different purposes: mentors provide guidance, peers offer collaboration opportunities, and mentees keep you connected to emerging trends and perspectives.

#### Best Practices
- Maintain relationships across multiple industries and functional areas
- Include people at different career stages from junior to executive level
- Balance internal organizational relationships with external industry connections
- Focus on quality of relationships rather than quantity of contacts

### The Value-First Networking Philosophy
The most effective networkers lead with value creation rather than personal benefit. This approach builds stronger, more sustainable relationships and creates genuine reciprocity.

#### Industry Usage
Top sales professionals and consultants use value-first approaches because they generate 60% more referrals and maintain 80% stronger long-term relationships. This philosophy differentiates authentic relationship builders from transactional networkers.

---

## Practical Examples

### Example 1: The Strategic Introduction Framework
Make introductions that create value for both parties while strengthening your relationships.

**Email Template:**
"Hi [Name A],

I'd like to introduce you to [Name B], who [relevant background/expertise]. Based on our conversation about [specific challenge/opportunity], I thought you two should connect.

[Name B], [Name A] is [relevant background] and has been working on [relevant project/challenge that connects to Name B's expertise].

I'll let you both take it from here. Hope this connection proves valuable for both of you!"

### Example 2: Industry Event Networking Strategy
Maximize relationship building at conferences and professional gatherings.

**The 5-5-5 Rule:**
- **5 Before**: Research 5 specific people you want to meet and prepare conversation starters
- **5 During**: Aim for 5 meaningful conversations rather than collecting 50 business cards
- **5 After**: Follow up with 5 specific next steps within 48 hours of the event

💡 **Pro Tip**: Use the "Coffee Chat Framework" - offer 15-minute virtual coffees to maintain relationships. This low-commitment format has 90% acceptance rates and builds genuine connections.

---

## Industry Adoption

### Why Companies Choose This Approach
Organizations encourage employee networking because it brings external perspectives, identifies partnership opportunities, and enhances company reputation. Companies with strong networking cultures show 35% faster innovation and 25% better market intelligence.

### Popular Tools and Libraries
- **LinkedIn Sales Navigator**: Advanced relationship mapping and engagement tools
- **Calendly**: Streamlined scheduling for networking conversations
- **Luma**: Event discovery and networking for professional gatherings

### Comparison with Alternatives
| Approach | Advantages | Use Cases |
|----------|------------|-----------|
| Digital-First Networking | Scalable, efficient, global reach | Remote work, emerging industries, international markets |
| Relationship-Based Networking | Deep trust, strong referrals, local influence | Traditional industries, local markets, senior-level positions |

---

## Key Takeaways

✅ **Essential Points**:
- Network portfolio diversity creates more opportunities than network size
- Value-first approaches build stronger, more sustainable professional relationships
- Systematic relationship maintenance requires intentional planning and follow-through
- Hidden job markets are accessed through relationship trust, not public applications

⚠️ **Common Pitfalls**:
- Reaching out to people only when you need something from them
- Focusing on collecting contacts rather than building genuine relationships

## Next Steps
Next, you'll learn leadership development principles that position you for advancement opportunities and enable you to influence others effectively.

---
`,
  duration: "45 minutes",
  difficulty: "intermediate" as const,
  objectives: [
    "Build diverse network portfolios",
    "Apply value-first relationship strategies",
    "Master event networking techniques",
    "Access hidden job markets"
  ],
  keyTopics: [
    "Relationship Building",
    "Network Diversity",
    "Value Creation",
    "Industry Events",
    "Follow-up Systems"
  ]
};